// useCancellations — list bookings in cancellation_requested or cancelled.
//
// The server has no dedicated /cancellations endpoint; we filter the
// bookings list. Two queries are exposed so the page can render two tabs
// (Pending Review = cancellation_requested; All Cancellations = cancelled
// + cancellation_requested).

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type { FilterRecord } from '../../../api/queryKeys';
import type { BookingListResponse } from '../../../types/api';

const STALE_30_S = 30_000;

export interface CancellationFilters {
  q?: string;
  driver?: number;
  date_from?: string;
  date_to?: string;
  page: number;
  limit: number;
}

interface FetchParams extends CancellationFilters {
  status: 'cancellation_requested' | 'cancelled';
}

async function fetchBookingsByStatus(params: FetchParams): Promise<BookingListResponse> {
  const res = await apiClient.get<BookingListResponse>(endpoints.bookings.list, {
    params,
  });
  if (!res.data) throw new Error('Bookings list response missing data');
  return res.data;
}

export function useCancellationsPending(filters: { page: number; limit: number }) {
  const filterRecord: FilterRecord = {
    ...filters,
    status: 'cancellation_requested',
    tab: 'pending',
  };
  return useQuery<BookingListResponse>({
    queryKey: queryKeys.cancellations.list(filterRecord),
    queryFn: () =>
      fetchBookingsByStatus({
        ...filters,
        status: 'cancellation_requested',
      }),
    staleTime: STALE_30_S,
  });
}

/** Bookings a driver's receipt completed after they were cancelled, not yet acknowledged. */
export function useCompletedAfterCancellation(filters: { page: number; limit: number }) {
  const filterRecord: FilterRecord = { ...filters, tab: 'conflicts' };
  return useQuery<BookingListResponse>({
    queryKey: queryKeys.cancellations.list(filterRecord),
    queryFn: async () => {
      const res = await apiClient.get<BookingListResponse>(endpoints.bookings.list, {
        params: { ...filters, completed_after_cancellation: 1 },
      });
      if (!res.data) throw new Error('Bookings list response missing data');
      return res.data;
    },
    staleTime: STALE_30_S,
  });
}

export function useCancellationsAll(filters: CancellationFilters) {
  const filterRecord: FilterRecord = {
    ...filters,
    status: 'cancelled',
    tab: 'all',
  };
  return useQuery<BookingListResponse>({
    queryKey: queryKeys.cancellations.list(filterRecord),
    queryFn: () =>
      fetchBookingsByStatus({
        ...filters,
        status: 'cancelled',
      }),
    staleTime: STALE_30_S,
  });
}
